import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import DairyQuery from '~/QueryHooks/DairyQuery'
import { handleErrors } from '~/utils/helpers'
import DairyService from '~/api/services/dairy'
import notify from '~/components/Notify'
import { useNavigate, useParams } from 'react-router'
import { Form } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import LocationForm from '../Managers/CreateManager/LocationForm'

const CreateEditCollectionPoint: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()

  const [isBusy, setIsBusy] = useState(false)

  const methods = useForm({
    mode: 'onSubmit'
  })

  const { control } = methods

  const {
    data: managersList,
    isLoading: isLoadingManagers
  } = DairyQuery.getManagersList()

  const {
    data: transportationAreasList,
    isLoading: isLoadingTransportAreas,
    refetch: fetchTransportList
  } = DairyQuery.getTransportationAreasList()

  const {
    data: collectionPointOptions,
    isLoading: isLoadingCollectionOptions,
    refetch: refetchCollectionOptions
  } = DairyQuery.getCollectionPointsOptions()

  const {
    data: editData,
    isLoading: isLoadingEdit,
    refetch: refetchEdit
  } = DairyQuery.editCollectionPoint(id)

  const {
    data: countries,
    isLoading: isLoadingCountries,
    refetch: fetchCountries
  } = DairyQuery.getCountriesList()

  useEffect(() => {
    fetchTransportList()
    refetchCollectionOptions()
    fetchCountries()
  }, [t])

  useEffect(() => {
    if (id) {
      refetchEdit()
    }
  }, [id, t])

  useEffect(() => {
    if (editData && id) {
      methods.reset({
        ...editData
      })
    }
  }, [editData, id])

  function renderForm() {
    return <>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          name={'uniqueId'}
          label={t('Collection point ID')}
          placeholder={t('Please enter Collection point ID')}
          rules={{
            required: t('Please enter Collection point ID'),
            maxLength: {
              value: 32,
              message: t('Max chart. is 32')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'name'}
          label={t('Collection point name')}
          placeholder={t('Please enter Collection point name')}
          rules={{
            required: t('Please enter Collection point name'),
            maxLength: {
              value: 100,
              message: t('Max chart. is 100')
            }
          }}
        />
      </Form.Group>
      <Form.Group widths={'equal'} style={{ alignItems: 'center' }}>
        <FormFields.Select
          control={control}
          name={'managers'}
          multiple
          options={managersList?.map(manager => ({ value: manager.id, text: manager.name })) || []}
          rules={{
            required: t('Please select at least one manager')
          }}
          label={t('Managers')}
          placeholder={t('Please select managers')}
        />
        <FormFields.Checkbox
          control={control}
          name={'businessUnit'}
          label={t('Business Unit')}
          defaultValue={0}
          style={{ marginTop: 20 }}
          checkboxValue={1}
        />

      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'transportAreaId'}
          options={transportationAreasList?.map(transportArea => ({ value: transportArea.id, text: transportArea.name })) || []}
          rules={{
            required: t('Please select transport area')
          }}
          label={t('Transportation Area')}
          placeholder={t('Please Transportation Area')}
        />
        <FormFields.Input
          control={control}
          name={'evsNumber'}
          label={t('EVS-Nr.')}
          placeholder={t('Please enter EVS-Nr.')}
          rules={{
            required: t('Please enter EVS-Nr.')
          }}
        />
        <FormFields.Input
          control={control}
          name={'agisId'}
          label={t('AGIS-ID')}
          placeholder={t('Please enter AGIS-ID')}
          rules={{
            required: t('Please enter AGIS-ID')
          }}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'languageId'}
          options={collectionPointOptions?.languages?.map(language => ({ value: language.id, text: language.label, flag: language.icon })) || []}
          rules={{
            required: t('Please select language')
          }}
          label={t('Language')}
          placeholder={t('Please select language')}
        />
        <FormFields.Select
          control={control}
          name={'collectionMethodId'}
          options={collectionPointOptions?.collectionMethods?.map(method => ({ value: method.id, text: method.label })) || []}
          label={t('Collection method')}
          placeholder={t('Please select collection method')}
        />
        <FormFields.Select
          control={control}
          name={'contractTypeId'}
          options={collectionPointOptions?.contractTypes?.map(type => ({ value: type.id, text: type.label })) || []}
          label={t('Contract type')}
          placeholder={t('Please select contract type')}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'billingGroupId'}
          options={collectionPointOptions?.billingGroups?.map(group => ({ value: group.id, text: group.name })) || []}
          label={t('Billing group')}
          placeholder={t('Please select billing group')}
        />
        <FormFields.Select
          control={control}
          name={'transportTariffId'}
          options={collectionPointOptions?.tranportTariffs?.map(tariff => ({ value: tariff.id, text: tariff.name })) || []}
          label={t('Transport tariff')}
          placeholder={t('Please select transport tariff')}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'milkTypeId'}
          options={collectionPointOptions?.milkTypes?.map(type => ({ value: type.id, text: type.name })) || []}
          rules={{
            required: t('Please select milk type')
          }}
          label={t('Milchtype')}
          placeholder={t('Please select milk type')}
        />
        <FormFields.Select
          control={control}
          name={'milkTypeCharacteristicId'}
          options={collectionPointOptions?.milkTypeCharacteristics?.map(char => ({ value: char.id, text: char.label })) || []}
          label={t('Milk type Characteristic')}
          placeholder={t('Please select milk type characteristic')}
        />
      </Form.Group>
      <LocationForm
        countries={countries || []}
        collectionSiteOptions={collectionPointOptions}
        useGoogleMapLocation
      />
    </>
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      if (id) {
        await DairyService.updateCollectionPoint(id, data)
        notify(t('Successfully updated collection point'), 'success')
      } else {
        await DairyService.storeCollectionPoint(data)
        notify(t('Successfully created collection point'), 'success')
      }
      navigate(RouteTypes.DAIRY_FACTORY_COLLECTION_POINTS)
    } catch (errors) {
      handleErrors(errors)
    }
    setIsBusy(false)
  }

  const isLoading = isLoadingManagers || isBusy || (id && isLoadingEdit) || isLoadingTransportAreas || isLoadingCollectionOptions || isLoadingCountries

  return <Layout
    title={id ? t('Edit collection point') : t('Create collection point')}
    onSubmit={methods.handleSubmit(onSubmit)}
    segmented
    isBusy={isLoading}
    close={RouteTypes.DAIRY_FACTORY_COLLECTION_POINTS}
    action={id ? t('Update collection point') : t('Add collection point')}
  >
    <FormProvider {...methods}>
      {renderForm()}
    </FormProvider>
  </Layout>
}
export default CreateEditCollectionPoint
